import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Divider,
} from '@mui/material';
import { ArrowBack, Policy } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Policy sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h3" component="h1">
            Privacy Policy
          </Typography>
        </Box>
        
        <Typography variant="body1" color="textSecondary">
          Last updated: October 2024
        </Typography>
      </Box>

      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>
            🔒 Your Privacy Matters
          </Typography>
          
          <Typography variant="body1" paragraph>
            2nd Opinion AI ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our medical report analysis service.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            📋 Information We Collect
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>Personal Information:</strong>
          </Typography>
          <ul>
            <li>Name and email address (from Google authentication)</li>
            <li>Profile information from your Google account</li>
            <li>Patient age and gender for medical analysis</li>
          </ul>

          <Typography variant="body1" paragraph>
            <strong>Medical Information:</strong>
          </Typography>
          <ul>
            <li>Medical reports you upload (PDF format)</li>
            <li>Additional questions about your medical reports</li>
            <li>AI-generated analysis results</li>
          </ul>

          <Typography variant="body1" paragraph>
            <strong>Technical Information:</strong>
          </Typography>
          <ul>
            <li>IP address and browser information</li>
            <li>Usage patterns and service interactions</li>
            <li>Error logs and system performance data</li>
          </ul>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            🤖 AI Processing & OpenAI Integration
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>Important Notice:</strong> Your medical data is processed by OpenAI (a US-based AI service) for analysis generation.
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>What this means:</strong>
          </Typography>
          <ul>
            <li>Your medical reports are sent to OpenAI servers in the United States</li>
            <li>OpenAI's AI models analyze your data to generate medical insights</li>
            <li>Data is encrypted during transmission and processing</li>
            <li>OpenAI does not retain your data after processing is complete</li>
            <li>Processing is subject to OpenAI's privacy policy and data handling practices</li>
          </ul>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            🛡️ How We Protect Your Information
          </Typography>
          
          <ul>
            <li><strong>Encryption:</strong> All data is encrypted in transit and at rest</li>
            <li><strong>Secure Storage:</strong> Files stored in Microsoft Azure with enterprise-grade security</li>
            <li><strong>Access Control:</strong> Strict access controls and authentication requirements</li>
            <li><strong>Data Minimization:</strong> We only collect information necessary for service provision</li>
            <li><strong>Regular Audits:</strong> Security practices are regularly reviewed and updated</li>
          </ul>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            💾 Data Retention
          </Typography>
          
          <ul>
            <li>Medical reports are stored for analysis and result delivery purposes</li>
            <li>Analysis results are retained for your access via email</li>
            <li>Account information is kept while your account is active</li>
            <li>You may request data deletion by contacting us</li>
          </ul>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            🌍 Geographic Restrictions
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>India-Only Service:</strong> This service is designed exclusively for users based in India. We have not implemented GDPR (European Union) or HIPAA (United States) compliance measures.
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>If you are located in the EU or US:</strong> Please do not use this service as it may not comply with your local privacy regulations.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            📧 Contact Us
          </Typography>
          
          <Typography variant="body1" paragraph>
            If you have questions about this Privacy Policy or our data practices, please contact us at:
          </Typography>
          
          <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 2 }}>
            <Typography variant="body2">
              <strong>Email:</strong> info@2ndopinionai.org<br />
              <strong>Subject:</strong> Privacy Policy Inquiry
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            🔄 Changes to This Policy
          </Typography>
          
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time. We will notify users of any material changes by email or through our service.
          </Typography>

          <Typography variant="body2" color="textSecondary">
            By using our service, you acknowledge that you have read and understood this Privacy Policy and agree to our data practices as described herein.
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PrivacyPolicy;




